import { useLayoutEffect, useState } from 'react';
import type { RefObject } from 'react';

/**
 * Measures the observed element's rendered width via ResizeObserver so a block
 * can drive its responsive layout off its own available content width rather
 * than the viewport.
 *
 * Content-width breakpoints keep the authoring preview and the exported player
 * identical regardless of surrounding chrome — e.g. the editor's assignment
 * panel narrows the canvas without changing `window.innerWidth`, which would
 * otherwise make viewport breakpoints fire at the wrong moment in preview.
 *
 * Returns the measured width in CSS pixels, or `undefined` until the first
 * measurement. Callers should treat `undefined` as the widest/desktop layout
 * so the block never flashes a compact layout before measuring.
 */
export function useContainerWidth(ref: RefObject<HTMLElement | null>): number | undefined {
  const [width, setWidth] = useState<number | undefined>(undefined);

  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return undefined;

    // jsdom (tests) and any non-browser host lack ResizeObserver; take a single
    // synchronous measurement so behavior is deterministic without it.
    if (typeof ResizeObserver === 'undefined') {
      setWidth(node.offsetWidth);
      return undefined;
    }

    const update = () => setWidth(node.offsetWidth);
    update();

    const observer = new ResizeObserver(update);
    observer.observe(node);
    return () => observer.disconnect();
  }, [ref]);

  return width;
}
